/**
 * LogoIcon - Code League badge SVG component
 * Reusable logo icon with size variants
 */

import React from 'react';

interface LogoIconProps {
  size?: 'small' | 'medium' | 'large' | 'hero';
  className?: string;
}

const sizeMap = {
  small: 24,
  medium: 32,
  large: 48,
  hero: 200
};

export const LogoIcon: React.FC<LogoIconProps> = ({ size = 'medium', className = '' }) => {
  const pixelSize = sizeMap[size];
  
  return (
    <svg 
      width={pixelSize} 
      height={pixelSize} 
      viewBox="0 0 256 256" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Code League logo"
    >
      <defs>
        <linearGradient id={`bg-classic-${pixelSize}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#020617"/>
          <stop offset="40%" stopColor="#050B24"/>
          <stop offset="100%" stopColor="#0B1029"/>
        </linearGradient>

        <linearGradient id={`border-classic-${pixelSize}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2563EB"/>
          <stop offset="50%" stopColor="#D946EF"/>
          <stop offset="100%" stopColor="#FF8A24"/>
        </linearGradient>

        <linearGradient id={`gold-classic-${pixelSize}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FACC15"/>
          <stop offset="50%" stopColor="#FFB74A"/>
          <stop offset="100%" stopColor="#FF8A24"/>
        </linearGradient>
      </defs>

      {/* Outer badge */}
      <rect x="10" y="10" width="236" height="236" rx="52" ry="52"
            fill={`url(#bg-classic-${pixelSize})`}
            stroke={`url(#border-classic-${pixelSize})`} strokeWidth="3"/>

      {/* Dark inner field */}
      <circle cx="128" cy="128" r="88"
              fill="#050B24"/>

      {/* Centered symbol group */}
      <g transform="translate(128,6)">
        {/* Curly braces { } */}
        <text x="-64" y="156"
              fontSize="112"
              textAnchor="middle"
              fontFamily="monospace"
              fill={`url(#gold-classic-${pixelSize})`}>{'{'}</text>

        <text x="64" y="156"
              fontSize="112"
              textAnchor="middle"
              fontFamily="monospace"
              fill={`url(#gold-classic-${pixelSize})`}>{'}'}</text>

        {/* Center glyph ">" */}
        <text x="0" y="136"
              fontSize="60"
              textAnchor="middle"
              fontFamily="monospace"
              fill="#FF8A24">&gt;</text>

        {/* Underscore */}
        <text x="0" y="146"
              fontSize="60"
              textAnchor="middle"
              fontFamily="monospace"
              fill="#00E5FF">_</text>
      </g>
    </svg>
  );
};

